import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaCheckCircle,
  FaEdit,
  FaExternalLinkAlt,
  FaPencilAlt,
  FaPlus,
  FaProjectDiagram,
  FaSearch,
} from "react-icons/fa";
import { apihubGet, apihubPost } from "../../api/apihub";
import {
  WHATSAPP_MANAGER_TEMPLATES,
  flowRecordToTemplate,
} from "../../data/whatsappFlowTemplates";
import { useProjectPhone } from "../../hooks/useProjectPhone";
import { migrateFrozenFlowJson } from "../../utils/whatsappFlowJson";
import WhatsAppFlowBuilder from "./WhatsAppFlowBuilder";
import StatusCapsule from "../StatusCapsule";

const FLOW_CATEGORIES = [
  "SIGN_UP",
  "SIGN_IN",
  "APPOINTMENT_BOOKING",
  "LEAD_GENERATION",
  "CONTACT_US",
  "CUSTOMER_SUPPORT",
  "SURVEY",
  "OTHER",
];

const DEFAULT_FLOW_JSON = `{
  "version": "7.3",
  "routing_model": { "WELCOME_SCREEN": [] },
  "screens": [
    {
      "id": "WELCOME_SCREEN",
      "title": "Welcome",
      "terminal": true,
      "success": true,
      "data": {},
      "layout": {
        "type": "SingleColumnLayout",
        "children": [
          { "type": "TextHeading", "text": "Hello World" },
          {
            "type": "Footer",
            "label": "Complete",
            "on-click-action": { "name": "complete", "payload": {} }
          }
        ]
      }
    }
  ]
}`;

const emptyForm = () => ({
  flowId: "",
  name: "",
  categories: ["OTHER"],
  publish: false,
  flowJson: DEFAULT_FLOW_JSON,
  templateId: "blank",
});

const Flows = ({ visualBuilder = false }) => {
  const { projectId, wabaId, needsWhatsAppSetup, projectLoading } =
    useProjectPhone();
  const [metaFlows, setMetaFlows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [pickingTemplate, setPickingTemplate] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchFlows = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const metaRes = await apihubGet(
        `/getInteractedCustomerDetail/getallflows?projectId=${projectId}&type=initial`,
      );
      setMetaFlows(Array.isArray(metaRes?.data) ? metaRes.data : []);
    } catch (err) {
      setMetaFlows([]);
      setError(err.message || "Failed to load flows");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchFlows();
  }, [fetchFlows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return metaFlows;
    return metaFlows.filter((f) => {
      const name = (f.name || "").toLowerCase();
      const id = String(f.id || f._id || "").toLowerCase();
      return name.includes(q) || id.includes(q);
    });
  }, [metaFlows, search]);

  const stats = useMemo(
    () => ({
      total: metaFlows.length,
      published: metaFlows.filter((f) => f.status === "PUBLISHED").length,
      draft: metaFlows.filter((f) => f.status === "DRAFT").length,
    }),
    [metaFlows],
  );

  const existingManagerTemplates = useMemo(() => {
    const seen = new Set();
    return metaFlows
      .map((flow) => flowRecordToTemplate(flow, "manager"))
      .filter((tpl) => {
        if (!tpl.metaFlowId || seen.has(tpl.metaFlowId)) return false;
        seen.add(tpl.metaFlowId);
        return true;
      });
  }, [metaFlows]);

  const openCreate = () => {
    setForm(emptyForm());
    setFormError("");
    setMessage("");
    setPickingTemplate(Boolean(visualBuilder));
    setModalOpen(true);
  };

  const openEdit = (flow) => {
    const json =
      flow.flow_json || flow.flowJson || flow.json
        ? JSON.stringify(flow.flow_json || flow.flowJson || flow.json, null, 2)
        : DEFAULT_FLOW_JSON;
    setForm({
      flowId: flow.id || flow._id || "",
      name: flow.name || "",
      categories:
        Array.isArray(flow.categories) && flow.categories.length
          ? flow.categories
          : ["OTHER"],
      publish: String(flow.status || "").toUpperCase() === "PUBLISHED",
      flowJson: json,
      templateId: "",
    });
    setFormError("");
    setPickingTemplate(false);
    setModalOpen(true);
  };

  const chooseTemplate = async (template) => {
    setLoadingTemplate(true);
    setFormError("");
    try {
      let json = template.flowJson
        ? typeof template.flowJson === "string"
          ? template.flowJson
          : JSON.stringify(template.flowJson, null, 2)
        : "";
      if (!json && template.metaFlowId) {
        const res = await apihubGet(
          `/getInteractedCustomerDetail/flowjson/${template.metaFlowId}?projectId=${projectId}`,
        );
        const data = res?.data || res;
        json =
          typeof data === "string" ? data : JSON.stringify(data, null, 2);
      }
      if (!json) json = DEFAULT_FLOW_JSON;
      JSON.parse(json);
      setForm({
        flowId: "",
        name: template.suggestedName || template.name || "",
        categories: template.categories?.length ? template.categories : ["OTHER"],
        publish: false,
        flowJson: json,
        templateId: template.id,
      });
      setPickingTemplate(false);
    } catch (err) {
      setFormError(err.message || "Could not load this template.");
    } finally {
      setLoadingTemplate(false);
    }
  };

  const toggleCategory = (category) => {
    setForm((prev) => {
      const has = prev.categories.includes(category);
      const next = has
        ? prev.categories.filter((item) => item !== category)
        : [...prev.categories, category];
      return { ...prev, categories: next.length ? next : ["OTHER"] };
    });
  };

  const onJsonFile = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      JSON.parse(text);
      setForm((prev) => ({ ...prev, flowJson: text }));
      setFormError("");
    } catch {
      setFormError("Uploaded file is not valid JSON.");
    }
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (!projectId) return;
    if (!form.name.trim()) {
      setFormError("Flow name is required.");
      return;
    }
    if (!form.categories.length) {
      setFormError("Select at least one category.");
      return;
    }
    let flowJsonPayload = form.flowJson;
    try {
      flowJsonPayload = JSON.stringify(
        migrateFrozenFlowJson(form.flowJson || "{}"),
        null,
        2,
      );
    } catch {
      setFormError("Flow JSON is invalid.");
      return;
    }

    setSaving(true);
    setFormError("");
    setError("");
    setMessage("");
    try {
      const res = await apihubPost("/getInteractedCustomerDetail/create-flow", {
        projectId,
        wabaId,
        flowId: form.flowId || undefined,
        name: form.name.trim(),
        categories: form.categories,
        publish: form.publish,
        flow_json: flowJsonPayload,
      });
      if (res?.success === false) {
        throw new Error(res.message || "Failed to save flow");
      }
      const warnings = res?.validation_errors || [];
      setMessage(
        warnings.length
          ? `${res?.message || "Flow saved"} (JSON has validation warnings)`
          : res?.message || "Flow saved successfully.",
      );
      setModalOpen(false);
      setForm(emptyForm());
      fetchFlows();
    } catch (err) {
      setFormError(err.message || "Failed to upload flow to WhatsApp");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="biz-panel-page">
      <div className="biz-panel-header biz-flows-header">
        <h1>
          <FaProjectDiagram />
          {visualBuilder ? "WhatsApp Flows" : "Flow Builder"}
        </h1>
        <div className="biz-flows-header-actions">
          <button
            type="button"
            className="biz-btn-primary"
            onClick={openCreate}
            disabled={projectLoading || needsWhatsAppSetup}
          >
            <FaPlus />
            {visualBuilder ? "Create with builder" : "Create Flow"}
          </button>
          <a
            href="https://business.facebook.com/latest/whatsapp_manager/flows"
            target="_blank"
            rel="noreferrer"
            className="biz-btn-secondary biz-flows-create-link"
          >
            <FaExternalLinkAlt />
            Open in Meta
          </a>
        </div>
      </div>

      {message && <p className="biz-banner biz-banner-success">{message}</p>}
      {needsWhatsAppSetup && (
        <p className="biz-banner biz-banner-error">
          Connect WhatsApp for this project before creating a flow.
        </p>
      )}

      <div className="biz-flows-stats">
        <div className="biz-flows-stat-card biz-flows-stat-total">
          <div className="biz-flows-stat-icon">
            <FaProjectDiagram />
          </div>
          <div className="biz-flows-stat-copy">
            <span>Meta flows</span>
            <strong>{stats.total}</strong>
          </div>
        </div>
        <div className="biz-flows-stat-card biz-flows-stat-active">
          <div className="biz-flows-stat-icon">
            <FaCheckCircle />
          </div>
          <div className="biz-flows-stat-copy">
            <span>Published</span>
            <strong>{stats.published}</strong>
          </div>
        </div>
        <div className="biz-flows-stat-card biz-flows-stat-draft">
          <div className="biz-flows-stat-icon">
            <FaPencilAlt />
          </div>
          <div className="biz-flows-stat-copy">
            <span>Draft</span>
            <strong>{stats.draft}</strong>
          </div>
        </div>
      </div>

      <div className="biz-panel-search">
        <FaSearch className="biz-panel-search-icon" />
        <input
          type="search"
          placeholder="Search by flow name or ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {error && <p className="biz-panel-error">{error}</p>}

      <div className="biz-table-wrap">
        <table className="biz-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Flow Name</th>
              <th>ID</th>
              <th>Status</th>
              <th>Category</th>
              <th className="biz-col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="biz-table-empty">
                  Loading flows…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="biz-table-empty">
                  No flows found.
                </td>
              </tr>
            ) : (
              filtered.map((flow, idx) => (
                <tr key={flow.id || flow._id || idx}>
                  <td>{idx + 1}</td>
                  <td>{flow.name || "Unnamed Flow"}</td>
                  <td className="biz-table-mono">
                    {flow.id || flow._id || "—"}
                  </td>
                  <td>
                    <StatusCapsule
                      value={flow.status || flow.meta?.status || "—"}
                    />
                  </td>
                  <td>
                    {(flow.categories || []).join(", ") || "—"}
                  </td>
                  <td className="biz-col-actions">
                    <div className="biz-table-actions">
                      <button
                        type="button"
                        className="biz-btn-icon"
                        title={visualBuilder ? "Edit in builder" : "Update"}
                        onClick={() => openEdit(flow)}
                      >
                        <FaEdit />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {modalOpen && visualBuilder && (
        <div className="wa-flow-builder-overlay">
          {pickingTemplate ? (
            <div className="wa-flow-builder-shell">
              <header className="wa-flow-builder-top">
                <div>
                  <h2>Create WhatsApp Flow</h2>
                  <p>Choose a WhatsApp Manager template, then customize it in the builder.</p>
                </div>
                <div className="wa-flow-builder-top-actions">
                  <button
                    type="button"
                    className="biz-btn-secondary"
                    onClick={() => {
                      setModalOpen(false);
                      setPickingTemplate(false);
                    }}
                    disabled={loadingTemplate}
                  >
                    Cancel
                  </button>
                </div>
              </header>
              {formError && <p className="biz-banner biz-banner-error">{formError}</p>}
              <div className="wa-flow-template-page">
                <h3>WhatsApp Manager templates</h3>
                <div className="wa-flow-template-grid">
                  {WHATSAPP_MANAGER_TEMPLATES.map((template) => (
                    <button
                      key={template.id}
                      type="button"
                      className="wa-flow-template-card"
                      disabled={loadingTemplate}
                      onClick={() => chooseTemplate(template)}
                    >
                      <strong>{template.name}</strong>
                      <span>{template.description}</span>
                      <em>{(template.categories || []).join(", ")}</em>
                    </button>
                  ))}
                </div>
                {existingManagerTemplates.length > 0 && (
                  <>
                    <h3>Your WhatsApp Manager flows</h3>
                    <div className="wa-flow-template-grid">
                      {existingManagerTemplates.map((template) => (
                        <button
                          key={template.id}
                          type="button"
                          className="wa-flow-template-card"
                          disabled={loadingTemplate}
                          onClick={() => chooseTemplate(template)}
                        >
                          <strong>{template.name}</strong>
                          <span>{template.description}</span>
                          <em>Existing flow</em>
                        </button>
                      ))}
                    </div>
                  </>
                )}
                {loadingTemplate && (
                  <p className="wa-flow-template-loading">Loading template…</p>
                )}
              </div>
            </div>
          ) : (
            <form className="wa-flow-builder-shell" onSubmit={onSave}>
              <header className="wa-flow-builder-top">
                <div>
                  <h2>{form.flowId ? "Edit WhatsApp Flow" : "Create WhatsApp Flow"}</h2>
                  <p>Drag WhatsApp components onto the screen, then upload to Meta.</p>
                </div>
                <div className="wa-flow-builder-top-actions">
                  {!form.flowId && (
                    <button
                      type="button"
                      className="biz-btn-secondary"
                      onClick={() => setPickingTemplate(true)}
                      disabled={saving}
                    >
                      Change template
                    </button>
                  )}
                  <button
                    type="button"
                    className="biz-btn-secondary"
                    onClick={() => {
                      setModalOpen(false);
                      setPickingTemplate(false);
                    }}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="biz-btn-primary" disabled={saving}>
                    {saving
                      ? "Uploading…"
                      : form.flowId
                        ? "Update on WhatsApp"
                        : "Create & upload"}
                  </button>
                </div>
              </header>
              {formError && <p className="biz-banner biz-banner-error">{formError}</p>}
              <div className="wa-flow-builder-meta">
                <label>
                  Flow name *
                  <input
                    value={form.name}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, name: e.target.value }))
                    }
                    required
                  />
                </label>
                <label className="wa-flow-check">
                  <input
                    type="checkbox"
                    checked={form.publish}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, publish: e.target.checked }))
                    }
                    disabled={Boolean(form.flowId)}
                  />
                  Publish immediately
                </label>
                <div className="biz-flows-categories">
                  {FLOW_CATEGORIES.map((category) => (
                    <label key={category} className="biz-flows-category">
                      <input
                        type="checkbox"
                        checked={form.categories.includes(category)}
                        onChange={() => toggleCategory(category)}
                      />
                      {category.replaceAll("_", " ")}
                    </label>
                  ))}
                </div>
              </div>
              <WhatsAppFlowBuilder
                key={form.templateId || form.flowId || "new-flow"}
                value={form.flowJson}
                onChange={(flowJson) =>
                  setForm((prev) => ({ ...prev, flowJson }))
                }
              />
            </form>
          )}
        </div>
      )}

      {modalOpen && !visualBuilder && (
        <div className="biz-modal-backdrop" onClick={() => !saving && setModalOpen(false)}>
          <div
            className="biz-modal-card biz-modal-wide"
            onClick={(e) => e.stopPropagation()}
          >
            <h2>{form.flowId ? "Update WhatsApp Flow" : "Create WhatsApp Flow"}</h2>
            {formError && <p className="biz-banner biz-banner-error">{formError}</p>}
            <form className="biz-form-grid" onSubmit={onSave}>
              <label className="biz-form-full">
                Flow name *
                <input
                  value={form.name}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  required
                />
              </label>

              <div className="biz-form-full">
                <span>Categories *</span>
                <div className="biz-flows-categories">
                  {FLOW_CATEGORIES.map((category) => (
                    <label key={category} className="biz-flows-category">
                      <input
                        type="checkbox"
                        checked={form.categories.includes(category)}
                        onChange={() => toggleCategory(category)}
                      />
                      {category.replaceAll("_", " ")}
                    </label>
                  ))}
                </div>
              </div>

              {!form.flowId && (
                <label className="biz-form-full">
                  WhatsApp Manager template
                  <select
                    value={form.templateId || "blank"}
                    onChange={(e) => {
                      const id = e.target.value;
                      const template =
                        WHATSAPP_MANAGER_TEMPLATES.find((item) => item.id === id) ||
                        existingManagerTemplates.find((item) => item.id === id);
                      if (template) chooseTemplate(template);
                    }}
                  >
                    {WHATSAPP_MANAGER_TEMPLATES.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                    {existingManagerTemplates.map((template) => (
                      <option key={template.id} value={template.id}>
                        Existing: {template.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <label>
                Upload Flow JSON
                <input
                  type="file"
                  accept="application/json,.json"
                  onChange={(e) => onJsonFile(e.target.files?.[0])}
                />
              </label>

              <label className="biz-checkbox-row">
                <input
                  type="checkbox"
                  checked={form.publish}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, publish: e.target.checked }))
                  }
                  disabled={Boolean(form.flowId)}
                />
                Publish immediately
              </label>

              <label className="biz-form-full">
                Flow JSON *
                <textarea
                  rows={12}
                  value={form.flowJson}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, flowJson: e.target.value }))
                  }
                  required
                />
              </label>

              <div className="biz-modal-actions biz-form-full">
                <button
                  type="button"
                  className="biz-btn-secondary"
                  onClick={() => setModalOpen(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button type="submit" className="biz-btn-primary" disabled={saving}>
                  {saving
                    ? "Uploading…"
                    : form.flowId
                      ? "Update on WhatsApp"
                      : "Create & upload"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Flows;
